import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { BiBriefcaseAlt2, BiChevronRight } from 'react-icons/bi';
import { Link } from 'react-router-dom';

import { Card, Skeleton, StatusPill } from '@/components';
import type { EventsProjectResponse } from '@/store/events/events-projects/events-projects.dto.ts';
import { useEventProjectsStore } from '@/store/events/events-projects/events-projects.store.ts';
import {
  byRecentActivity,
  lastActivityLabel,
  stageChip,
} from '@pages/EventProjectsPage/projectsModel';

import s from './ProjectsCard.module.scss';

const PROJECTS_PATH = '/manage/projects';

const VISIBLE = 4;

const itemCount = (project: EventsProjectResponse) =>
  (project.tasks?.length ?? 0) +
  (project.ways?.length ?? 0) +
  (project.vendors?.length ?? 0) +
  (project.files?.length ?? 0) +
  (project.conversations?.length ?? 0);

const ProjectsCard = ({
  className,
  headDivider,
}: {
  className?: string;
  headDivider?: boolean;
}) => {
  const { eventProjects, getAllEventProjects, isLoading } = useEventProjectsStore();

  useEffect(() => {
    void getAllEventProjects();
  }, [getAllEventProjects]);

  const recent = useMemo(
    () => [...eventProjects].sort(byRecentActivity).slice(0, VISIBLE),
    [eventProjects],
  );

  return (
    <Card as="section" ariaLabel="Projects" className={clsx(s.card, className)}>
      <div className={clsx(s.head, headDivider && s.headDivided)}>
        <BiBriefcaseAlt2 className={s.headIcon} aria-hidden="true" />
        <h2 className="headline-small">Projects</h2>
        <Link className={s.headLink} to={PROJECTS_PATH}>
          See all projects <BiChevronRight aria-hidden="true" />
        </Link>
      </div>

      {isLoading && eventProjects.length === 0 ? (
        <ul className={s.rows} aria-busy="true">
          {Array.from({ length: VISIBLE }, (_, i) => (
            <li key={i} className={s.skeletonRow}>
              <Skeleton variant="text" width="70%" height={14} />
              <Skeleton variant="text" width="45%" height={12} />
            </li>
          ))}
        </ul>
      ) : recent.length === 0 ? (
        <p className={s.empty}>Please add the first project for this vessel.</p>
      ) : (
        <ul className={s.rows}>
          {recent.map((project) => {
            const count = itemCount(project);
            const stage = stageChip(project.status);

            return (
              <li key={project.id}>
                <Link className={s.row} to={`${PROJECTS_PATH}/${project.id}`}>
                  <span className={s.rowTitle}>{project.name}</span>
                  <span className={s.meta}>
                    {count > 0 && (
                      <>
                        <span>
                          {count} {count === 1 ? 'item' : 'items'}
                        </span>
                        <span className={s.dot} aria-hidden="true">
                          ·
                        </span>
                      </>
                    )}
                    <span>{lastActivityLabel(project)}</span>
                    <StatusPill className={s.stage} label={stage.label} tone={stage.tone} />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

export default ProjectsCard;
